// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { SpinnerMethods } from '@/mixins/helpers/SpinnerMethods'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'

// Import the events
import { GetUser } from '@/events/nestd-residents/GetUser'

// Import firebase
import { propertiesModel, residentsModel } from '@/firebase/firebase-config'
import { residentsUsersModel } from '@/firebase/residents-firebase-config'
import moment from 'moment'

export const CreateLease = {

    /*
	**  Register the mixins
	*/

	mixins: [

        BugsnagService,
        SpinnerMethods,
        GenerateUID,
        GetUser

    ],

    /*
	**  Set up the data
	*/

	data: () => ({

        processing: false,
        create_lease_error: false,
        new_lease_props_set: false,
        new_lease_created: false,
        property_resident_updated: false,
        resident_updated: false,
        unit_updated: false,
        resident_user_updated: false,
        get_resident_user_complete: false

    }),

    /*
    **  Watch em'
    */

    watch: {

        create_lease_error: {

            immediate: true, // Load on created
            handler(create_lease_error) {

                /*
                **  Handle the error
                **  @method: @here
                */

                if (create_lease_error) this.handleCreateLeaseError();

            }

        },

        get_nestd_resident_complete: {

            immediate: true, // Load on created
            handler(get_nestd_resident_complete) {

                /*
                **  Set the lease props
                **  @method: @here
                */

                if (!this.create_lease_error && get_nestd_resident_complete) this.setNewLeaseProps();

            }

        },

        new_lease_props_set: {

            immediate: true, // Load on created
            handler(new_lease_props_set) {

                /*
                **  Create the lease
                **  @method: @here
                */

                if (new_lease_props_set && !this.create_lease_error) this.createLeaseDocument();

            }

        },

        new_lease_created: {

            immediate: true, // Load on created
            handler(new_lease_created) {

                if (!this.create_lease_error && new_lease_created && this.new_lease.attributes.status == 'active') {
                    
                    /*
                    **  Update the property resident
                    **  @method: @here
                    */

                    this.updatePropertyResident();

                } else if (!this.create_lease_error && new_lease_created) {

                    /*
                    **  Skip ahead and complete the event
                    **  @prop: @here
                    */

                    this.resident_user_updated = true;

                }

            }

        },

        property_resident_updated: {

            immediate: true, // Load on created
            handler(property_resident_updated) {

                /*
                **  Update the resident
                **  @method: @here
                */

                if (!this.create_lease_error && property_resident_updated && this.new_lease.attributes.status == 'active') this.updateResident();

            }

        },

        resident_updated: {

            immediate: true, // Load on created
            handler(resident_updated) {

                /*
                **  Update the unit
                **  @method: @here
                */

                if (!this.create_lease_error && resident_updated && this.new_lease.attributes.status == 'active') this.updateUnit();

            }

        },

        unit_updated: {

            immediate: true, // Load on created
            handler(unit_updated) {

                /*
                **  Update the resident site user
                **  @method: @here
                */

                if (!this.create_lease_error && unit_updated && this.new_lease.attributes.status == 'active' && this.resident.attributes.referral.registered) this.updateResidentSiteUser();

            }

        },

        resident_user_updated: {

            immediate: true, // Load on created
            handler(resident_user_updated) {

                /*
                **  Update the resident site user
                **  @method: @here
                */

                if (!this.create_lease_error && resident_user_updated) this.completeCreateLeaseEvent();

            }

        }

    },

    /*
	**  Methods
    */
    
    methods: {

        createLeaseHandler: function() {

            try {

                /*
                **  If processing, return
                **  @prop: @here
                */

                if (this.processing) return;

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = true;

                /*
                **  Show the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.showSpinner();

                /*
                **  Get the nestd residents portal user
                **  @service: @Firebase/Firestore
                */

                this.getUserHandler(this.resident.id);

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Create a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'zzAHNIlVgAyQVwvmpz40',
                    error: e
                });

            }

        },

        handleCreateLeaseError: function() {

            try {

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = false;
    
                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.hideSpinner();

                /*
                **  Throw error message
                **  @service: SweetAlert2
                */

                this.$swal('Uh Oh!', 'We ran into some problems while trying to create the lease.', 'error');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Handle an Error When Creating a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'jFgmES5Ny1e1mywvp8zu',
                    error: e
                });

            }

        },

        setNewLeaseProps: function() {

            try {

                /*
                **  Generate UID
                **  @prop: @Repositories/Leases/LeasesRepository
                */

				this.new_lease.id = this.generateUID();

                /*
                **  Convert the term dates
                **  @props: @Repositories/Leases/LeasesRepository
                */

                this.new_lease.attributes.details.start_date = moment(this.new_lease.attributes.details.start_date).unix();
                this.new_lease.attributes.details.end_date = moment(this.new_lease.attributes.details.end_date).unix();
                
                /*
                **  Convert the notice date
                **  @prop: @Repositories/Leases/LeasesRepository
                */

                if (this.new_lease.attributes.renewal.notice_date) this.new_lease.attributes.renewal.notice_date = moment(this.new_lease.attributes.renewal.notice_date).unix();

                /*
                **  Set the relationships
                **  @prop: @Repositories/Leases/LeasesRepository
                */

                this.new_lease.relationships.property = this.$route.params.id;
                this.new_lease.relationships.unit = this.resident.relationships.unit;
                this.new_lease.relationships.resident = this.resident.id;
                this.new_lease.relationships.unit = this.resident.relationships.unit;

                /*
                **  Set the attribuutes
                **  @prop: @Repositories/Leases/LeasesRepository
                */

                this.new_lease.attributes.unit.number = this.unit.attributes.number;
                this.new_lease.attributes.resident.first_name = this.resident.attributes.contact_information.first_name;
                this.new_lease.attributes.resident.last_name = this.resident.attributes.contact_information.last_name;

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.new_lease_props_set = true;

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_lease_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Set the New Lease Props When Creating a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'S97yRWiEwR8z85TVr7KL',
                    error: e
                });

            }

        },

        createLeaseDocument: function() {

            try {

                /*
                **  Create the lease doc
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.$route.params.id).collection('leases').doc(this.new_lease.id).set(this.new_lease)
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.new_lease_created = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_lease_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Create the Lease Document When Creating a Lease.',
                        errorType: 'Application',
                        section: 'Create Lease',
                        uid: 'meintTwIfM2u8JvM806x',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_lease_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Create the Lease Document When Creating a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: '2FIGgDHBXWmqxGjMkYgH',
                    error: e
                });

            }

        },

        updatePropertyResident: function() {

            try {

                /*
                **  Update the property resident doc
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.$route.params.id).collection('residents').doc(this.resident.id).update({

                    'relationships.lease': this.new_lease.id,
                    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.property_resident_updated = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_lease_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Update the Property Resident Document When Creating a Lease.',
                        errorType: 'Application',
                        section: 'Create Lease',
                        uid: 'lpXm4VljS3swjbVMSwDL',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_lease_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update the Property Resident When Creating a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'IQHiPsaf5QZ9SXTC8yJM',
                    error: e
                });

            }

        },

        updateResident: function() {

            try {

                /*
                **  Update the resident doc
                **  @service: @Firebase/Firestore
                */

                residentsModel.doc(this.resident.id).update({

                    'relationships.lease': this.new_lease.id,
                    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.resident_updated = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_lease_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Update the Resident Document When Creating a Lease.',
                        errorType: 'Application',
                        section: 'Create Lease',
                        uid: 'IDqRpJC2MF8XXrqM687F',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_lease_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update the Resident When Creating a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'G3qN0fRXDwfhry58lWhK',
                    error: e
                });

            }

        },

        updateUnit: function() {

            try {

                /*
                **  Update the resident doc
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.$route.params.id).collection('units').doc(this.unit.id).update({

                    'relationships.lease': this.new_lease.id,
                    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.unit_updated = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_lease_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Update the Unit Document When Creating a Lease.',
                        errorType: 'Application',
                        section: 'Create Lease',
                        uid: 'G3qN0fRXDwfhry58lWhK',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_lease_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update the Unit When Creating a Lease.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'UhYgsoSposZkuEmfjVXi',
                    error: e
                });

            }

        },

        updateResidentSiteUser: function() {

            try {

                /*
                **  Update the resident
                **  @service: @Firebase/Firestore
                */

                residentsUsersModel.doc(this.residents_user.id).update({
                    'relationships.lease': this.new_lease.id,
                    'meta.updated_at': moment().unix()
                })
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.resident_user_updated = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_lease_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Update a Nestd Resident User Document in Firestore.',
                        errorType: 'Application',
                        section: 'Create Lease',
                        uid: 'z2466ELciO9XCv9cSXj4',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_lease_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update a Nestd Resident User Document in Firestore.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'BxhaDCMvLubBD7ImKiAY',
                    error: e
                });

            }

        },

        completeCreateLeaseEvent: function() {

            try {

                /*
                **  Reset the validation props
                **  @prop: Components/Property/Leases/AddLeaseMenu
                */

                this.resetValidationProps();

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = false;

                /*
                **  Reset the form props
                **  @prop: @here
                */

                this.create_lease_error = false;
                this.new_lease_props_set = false;
                this.new_lease_created = false;
                this.property_resident_updated = false;
                this.resident_updated = false;
                this.unit_updated = false;
                this.resident_user_updated = false;
                this.get_resident_user_complete = false;

                /*
                **  Reset the new_lease prop
                **  @method: @Repositories/Leases/LeasesRepository
                */

                this.resetNewLeaseProp();

                /*
                **  Close the menu
                **  @method: Components/Property/Leases/AddLeaseMenu
                */

                this.closeMenu();

                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.hideSpinner();

                /*
                **  Throw success message
                **  @service: SweetAlert2
                */

                this.$swal('Success!', 'Your lease has been created.', 'success');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Complete the Create Lease Event.',
                    errorType: 'Application',
                    section: 'Create Lease',
                    uid: 'zTlNcV5vqzKjFdfY7XKH',
                    error: e
                });

            }

        },

    }

}